import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import ManageImagesGallerySection from "../../sections/manage/ManageImagesGallerySection";
import ManageFullDescriptionSection from "../../sections/manage/ManageFullDescriptionSection";
import ManageHighlightsSection from "../../sections/manage/ManageHighlightsSection";
import ManageInclusionsSection from "../../sections/manage/ManageInclusionsSection";
import ManageExclusionsSection from "../../sections/manage/ManageExclusionsSection";
import ManageLocationsSection from "../../sections/manage/ManageLocationsSection";
import ManageExtraInfoSection from "../../sections/manage/ManageExtraInfoSection";
import { usePackage } from "../../hooks/usePackage";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setTourEdit } from "../../reducer/tourEditReducer";
import { Paths } from "../../router/Paths";
import RoleBasedComponent from "../../components/RoleBasedComponent";
import TourOptions from "../../components/manage/TourOptions";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { toast } from "react-toastify";

export default function ProductDetailPage() {
  const params = useParams();
  const id = params.id;

  const { getPackagesById, updatePackage, approvePackage, rejectPackage } =
    usePackage();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { packageData } = useSelector((state: RootState) => state.tour);

  useEffect(() => {
    if (id && !(location.state && location.state.fromEdit)) {
      setLoading(true);
      getPackagesById({ setLoading }, id).finally(() => setLoading(false));
    } else if (location.state && location.state.fromEdit) {
      setLoading(false);
    }
  }, [id, location.state]);

  const handleSubmit = async () => {
    console.log("handleSubmit called", { id, packageData });
    if (!packageData) {
      console.error("No packageData in handleSubmit");
      toast.error("No package data to update.");
      return;
    }
    if (!id) {
      console.error("No id in handleSubmit");
      toast.error("No package ID to update.");
      return;
    }
    setSubmitLoading(true);
    try {
      // Deep copy and filter optionIds to IDs only
      const payloadCopy = JSON.parse(JSON.stringify(packageData));
      if (Array.isArray(payloadCopy.optionIds)) {
        payloadCopy.optionIds = payloadCopy.optionIds.map((item: any) =>
          typeof item === "object" && item !== null && "_id" in item
            ? item._id
            : item
        );
      }
      console.log("Calling updatePackage with:", id, payloadCopy);
      await updatePackage(id, payloadCopy);
    } catch (error) {
      console.error("Error updating package:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleApprove = () => {
    setSelectedAction("approve");
    setShowModal(true);
    setRejectionReason("");
  };

  const handleReject = () => {
    setSelectedAction("reject");
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAction(null);
    setRejectionReason("");
  };

  const handleActionConfirm = async () => {
    if (!selectedAction || !id) return;
    setSubmitLoading(true);
    try {
      if (selectedAction === "approve") {
        await approvePackage(id);
        toast.success("Package approved successfully");
      } else if (selectedAction === "reject") {
        await rejectPackage(id, { reasonForRejection: rejectionReason });
        toast.success("Package rejected successfully");
      }
      // Refresh package data
      await getPackagesById({ setLoading }, id);
      setShowModal(false);
      setSelectedAction(null);
      setRejectionReason("");
    } catch (error) {
      toast.error("Failed to perform action");
      console.error("Error:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = () => {
    dispatch(setTourEdit(packageData._id));
    navigate(Paths.tourManagementPackageName, {
      state: { packageData },
    });
  };

  return (
    <div className="font-main">
      <Navbar />
      <div className="flex flex-col min-h-screen bg-gray-100">
        <div className="w-full max-w-[1308px] max-xl:px-4 my-10 mx-auto">
          {loading ? (
            <div className="flex justify-center items-center h-96">
              <CircularProgress />
            </div>
          ) : !packageData ? (
            <div className="text-center text-gray-500 py-20">
              No package data found.
            </div>
          ) : (
            <>
              <div className="mb-4">
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    style={{ color: "black" }}
                    href="/manage/product"
                  >
                    Products
                  </Link>
                  <span className="text-primary">
                    {packageData?.title || "Product Detail"}
                  </span>
                </Breadcrumbs>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full mb-3 sm:mb-6">
                <p className="font-bold text-lg md:text-2xl w-full max-w-[633px] flex items-center gap-4">
                  {packageData?.title}
                  <RoleBasedComponent allowedRoles={["supplier"]}>
                    <button
                      onClick={handleEdit}
                      className="text-sm text-primary underline font-bold"
                    >
                      Edit
                    </button>
                  </RoleBasedComponent>
                </p>
                <div className="flex justify-end w-full">
                  <RoleBasedComponent allowedRoles={["supplier"]}>
                    <button
                      className="bg-primary text-white rounded-lg text-sm md:text-lg font-bold py-2.5 w-full max-w-[164px]"
                      onClick={handleSubmit}
                      disabled={submitLoading}
                    >
                      {submitLoading ? "Submitting..." : "Submit"}
                    </button>
                  </RoleBasedComponent>
                  <RoleBasedComponent allowedRoles={["admin"]}>
                    <button
                      className="bg-primary text-white rounded-lg text-sm md:text-lg font-bold py-2.5 w-full max-w-[164px] mr-2"
                      onClick={handleApprove}
                      disabled={submitLoading}
                    >
                      Approve
                    </button>
                    <button
                      className="bg-black text-white rounded-lg text-sm md:text-lg font-bold py-2.5 w-full max-w-[164px]"
                      onClick={handleReject}
                      disabled={submitLoading}
                    >
                      Reject
                    </button>
                  </RoleBasedComponent>
                </div>
              </div>
              {/* images gallery */}
              <ManageImagesGallerySection packageData={packageData} />
              {/* full description  */}
              <ManageFullDescriptionSection packageData={packageData} />
              <hr className="my-4 border-[#BCC7D366]" />
              {/* highlights */}
              <ManageHighlightsSection packageData={packageData} />
              <hr className="my-4 border-[#BCC7D366]" />
              {/* inclusions */}
              <ManageInclusionsSection packageData={packageData} />
              <hr className="my-4 border-[#BCC7D366]" />
              {/* exclusions */}
              <ManageExclusionsSection packageData={packageData} />
              <hr className="my-4 border-[#BCC7D366]" />
              {/* locations */}
              <ManageLocationsSection packageData={packageData} />
              <hr className="my-4 border-[#BCC7D366]" />
              {/* extra info */}
              <ManageExtraInfoSection packageData={packageData} />
              <hr className="my-4 border-[#BCC7D366]" />
              {id && (
                <TourOptions options={packageData?.optionIds} packageId={id} />
              )}
              {/* Modal for Confirming Actions */}
              {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                    <h2 className="text-lg font-semibold mb-4">
                      {selectedAction === "approve" && "Approve Product"}
                      {selectedAction === "reject" && "Reject Product"}
                    </h2>
                    <p className="mb-4">
                      {selectedAction === "approve" &&
                        "Are you sure you want to approve this product?"}
                      {selectedAction === "reject" &&
                        "Are you sure you want to reject this product?"}
                    </p>
                    {selectedAction === "reject" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">
                          Reason for rejection
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Enter reason"
                        />
                      </div>
                    )}
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={handleModalClose}
                        className="px-4 py-2 rounded text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                        disabled={submitLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleActionConfirm}
                        className={`px-4 py-2 rounded text-sm text-white ${
                          selectedAction === "approve"
                            ? "bg-primary"
                            : "bg-black"
                        }`}
                        disabled={
                          submitLoading ||
                          (selectedAction === "reject" &&
                            !rejectionReason.trim())
                        }
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
